import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { 
  Gift, Wallet, Star, ArrowRight, Check, 
  Storefront, Users, Crown, Sparkle, Lightning,
  CreditCard, ArrowDown, CurrencyBtc, ArrowsClockwise,
  UserPlus, QrCode, LockKey, HandHeart
} from '@phosphor-icons/react';
import GlobalNav from '../components/GlobalNav';
import PremiumCard from '../components/PremiumCard';
import { useLanguage } from '../contexts/LanguageContext';

// Animated Counter
const Counter = ({ end, duration = 2, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (!isInView) return;
    let start = 0;
    const increment = end / (duration * 60);
    const timer = setInterval(() => {
      start += increment;
      if (start >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 1000 / 60);
    return () => clearInterval(timer);
  }, [isInView, end, duration]);

  return <span ref={ref}>{count.toLocaleString()}{suffix}</span>;
};

// Section wrapper
const Section = ({ children, className = '', id = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <motion.section
      ref={ref}
      id={id}
      initial={{ opacity: 0, y: 60 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, ease: 'easeOut' }}
      className={className}
    >
      {children}
    </motion.section>
  );
};

const Landing = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start']
  });

  const heroOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const heroScale = useTransform(scrollYProgress, [0, 0.5], [1, 0.95]);
  const heroY = useTransform(scrollYProgress, [0, 0.5], [0, 100]);

  const stats = [
    { value: 2000, suffix: '+', labelKey: 'stats_shops' },
    { value: 45, suffix: '', labelKey: 'stats_countries' },
    { value: 120, suffix: 'K+', labelKey: 'stats_members' },
  ];

  return (
    <div className="min-h-screen bg-[#0a1610] text-white overflow-x-hidden">
      <GlobalNav />

      {/* HERO */}
      <motion.section
        ref={heroRef}
        style={{ opacity: heroOpacity, scale: heroScale, y: heroY }}
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
      >
        {/* Background */}
        <div className="absolute inset-0">
          <motion.div
            className="absolute top-1/4 -left-32 w-96 h-96 bg-[#145A3A]/30 rounded-full blur-[120px]"
            animate={{ x: [0, 50, 0], y: [0, 30, 0] }}
            transition={{ duration: 8, repeat: Infinity }}
          />
          <motion.div
            className="absolute bottom-1/4 -right-32 w-96 h-96 bg-[#C3A365]/20 rounded-full blur-[120px]"
            animate={{ x: [0, -50, 0], y: [0, -30, 0] }}
            transition={{ duration: 10, repeat: Infinity }}
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-32 pb-20 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center gap-2 bg-[#1E2F26] border border-[#C3A365]/30 px-4 py-2 rounded-full mb-8"
          >
            <LockKey size={16} className="text-[#C3A365]" weight="fill" />
            <span className="text-sm text-[#C3A365]">{t('hero_badge')}</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-5xl sm:text-6xl md:text-7xl font-light tracking-tight leading-[1.1] mb-8"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            {t('hero_title_1')}<br />
            <span className="text-[#C3A365]">{t('hero_title_2')}</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="text-lg text-[#8BA89A] max-w-2xl mx-auto mb-12"
          >
            {t('hero_subtitle')}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8"
          >
            <motion.button
              onClick={() => navigate('/offrir')}
              className="group px-8 py-4 bg-[#C3A365] text-[#0F1A14] font-semibold text-lg"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <span className="flex items-center gap-2">
                <Gift size={20} />
                {t('hero_cta')}
                <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
              </span>
            </motion.button>

            <motion.button
              onClick={() => document.getElementById('how-it-works').scrollIntoView({ behavior: 'smooth' })}
              className="flex items-center gap-3 px-8 py-4 border border-[#3E5248] text-white hover:border-[#C3A365]/50 transition-colors"
              whileHover={{ scale: 1.02 }}
            >
              {t('hero_cta_secondary')}
            </motion.button>
          </motion.div>

          {/* Access note */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="text-[#8BA89A] text-sm mb-16"
          >
            <HandHeart size={16} className="inline mr-2 text-[#C3A365]" />
            {t('hero_received')} <button onClick={() => navigate('/activer')} className="text-[#C3A365] underline">{t('hero_activate')}</button>
          </motion.p>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
            className="grid grid-cols-3 gap-8 max-w-lg mx-auto"
          >
            {stats.map((stat, i) => (
              <div key={i} className="text-center">
                <p className="text-3xl font-light text-white">
                  <Counter end={stat.value} suffix={stat.suffix} />
                </p>
                <p className="text-xs text-[#8BA89A]">{t(stat.labelKey)}</p>
              </div>
            ))}
          </motion.div>

          {/* Scroll */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
            className="absolute bottom-10 left-1/2 -translate-x-1/2"
          >
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="text-[#8BA89A]"
            >
              <ArrowDown size={24} />
            </motion.div>
          </motion.div>
        </div>
      </motion.section>

      {/* THE ONE CARD - Main concept */}
      <Section className="py-24 px-6 bg-[#0F1A14]" id="how-it-works">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#C3A365] text-sm tracking-[0.2em] uppercase font-semibold mb-4">
              {t('section_concept')}
            </p>
            <h2 className="text-4xl sm:text-5xl font-light tracking-tight mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {t('section_one_card')}
            </h2>
            <p className="text-[#8BA89A] max-w-xl mx-auto">
              {t('section_one_card_desc')}
            </p>
          </div>

          {/* The Card Visual - Premium Design */}
          <div className="max-w-md mx-auto mb-16">
            <PremiumCard 
              balance={127.50}
              tier="Reserve"
              cardNumber="•••• •••• •••• 7492"
              size="md"
            />
          </div>

          {/* Three uses */}
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Gift,
                titleKey: 'card_gift',
                descKey: 'card_gift_desc',
                color: '#C3A365'
              },
              {
                icon: Star,
                titleKey: 'card_loyalty',
                descKey: 'card_loyalty_desc',
                color: '#C3A365'
              },
              {
                icon: CreditCard,
                titleKey: 'card_payment',
                descKey: 'card_payment_desc',
                color: '#C3A365'
              }
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  viewport={{ once: true }}
                  className="text-center p-6 bg-[#18261E] border border-[#283D32]"
                >
                  <div className="w-14 h-14 rounded-xl bg-[#C3A365]/10 flex items-center justify-center mx-auto mb-4">
                    <Icon size={28} className="text-[#C3A365]" weight="duotone" />
                  </div>
                  <h3 className="text-xl text-white font-medium mb-2">{t(item.titleKey)}</h3>
                  <p className="text-[#8BA89A] text-sm">{t(item.descKey)}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </Section>

      {/* HOW TO JOIN - Cooptation */}
      <Section className="py-24 px-6 bg-[#18261E]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#C3A365] text-sm tracking-[0.2em] uppercase font-semibold mb-4">
              {t('section_join')}
            </p>
            <h2 className="text-3xl sm:text-4xl font-light tracking-tight mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {t('section_invitation')}
            </h2>
            <p className="text-[#8BA89A] max-w-xl mx-auto">
              {t('section_invitation_desc')}
            </p>
          </div>

          {/* The flow */}
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: '1',
                icon: Gift,
                titleKey: 'join_step1_title',
                descKey: 'join_step1_desc'
              },
              {
                step: '2',
                icon: UserPlus,
                titleKey: 'join_step2_title',
                descKey: 'join_step2_desc'
              },
              {
                step: '3',
                icon: Sparkle,
                titleKey: 'join_step3_title',
                descKey: 'join_step3_desc'
              }
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.15 }}
                  viewport={{ once: true }}
                  className="relative"
                >
                  {i < 2 && (
                    <div className="hidden md:block absolute top-12 left-[60%] w-[80%] h-px bg-gradient-to-r from-[#C3A365]/50 to-transparent" />
                  )}
                  <div className="text-center">
                    <div className="w-24 h-24 rounded-full bg-[#C3A365]/10 border border-[#C3A365]/30 flex items-center justify-center mx-auto mb-6 relative">
                      <Icon size={36} className="text-[#C3A365]" weight="duotone" />
                      <span className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-[#C3A365] text-[#0F1A14] flex items-center justify-center font-bold text-sm">
                        {item.step}
                      </span>
                    </div>
                    <h3 className="text-xl text-white font-medium mb-2">{t(item.titleKey)}</h3>
                    <p className="text-[#8BA89A]">{t(item.descKey)}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <p className="text-[#8BA89A] mb-6">
              {t('no_card_yet')}
            </p>
            <button
              onClick={() => navigate('/demander')}
              className="inline-flex items-center gap-2 px-6 py-3 border border-[#C3A365] text-[#C3A365] font-medium hover:bg-[#C3A365] hover:text-[#0F1A14] transition-all"
            >
              {t('request_invitation')}
            </button>
          </div>
        </div>
      </Section>

      {/* TOP UP - Recharge */}
      <Section className="py-24 px-6 bg-[#0F1A14]">
        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[#C3A365] text-sm tracking-[0.2em] uppercase font-semibold mb-4">
                {t('section_topup')}
              </p>
              <h2 className="text-3xl sm:text-4xl font-light tracking-tight mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                {t('section_topup_title')}
              </h2>
              <p className="text-[#8BA89A] mb-8 leading-relaxed">
                {t('section_topup_desc')}
              </p>

              <div className="space-y-4">
                <div className="flex items-center gap-4 p-4 bg-[#18261E] border border-[#283D32] rounded-lg">
                  <div className="w-12 h-12 rounded-lg bg-[#C3A365]/10 flex items-center justify-center">
                    <CreditCard size={24} className="text-[#C3A365]" />
                  </div>
                  <div>
                    <p className="text-white font-medium">{t('topup_card')}</p>
                    <p className="text-[#8BA89A] text-sm">{t('topup_card_desc')}</p>
                  </div>
                </div>

                <div className="flex items-center gap-4 p-4 bg-[#18261E] border border-[#283D32] rounded-lg">
                  <div className="w-12 h-12 rounded-lg bg-[#627EEA]/20 flex items-center justify-center">
                    <CurrencyBtc size={24} className="text-[#627EEA]" />
                  </div>
                  <div>
                    <p className="text-white font-medium">{t('topup_crypto')}</p>
                    <p className="text-[#8BA89A] text-sm">{t('topup_crypto_desc')}</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 p-4 bg-[#C3A365]/10 border border-[#C3A365]/30 rounded-lg">
                <p className="text-[#C3A365] text-sm">
                  <Lightning size={16} className="inline mr-2" weight="fill" />
                  {t('topup_no_fees')}
                </p>
              </div>
            </div>

            <div className="bg-[#18261E] border border-[#283D32] p-8 rounded-xl">
              <h3 className="text-xl text-white font-medium mb-6 text-center">{t('topup_amounts')}</h3>
              <div className="grid grid-cols-3 gap-3 mb-6">
                {[10, 25, 50, 75, 100, 200].map((amount) => (
                  <div
                    key={amount}
                    className="aspect-square flex items-center justify-center bg-[#1E2F26] border border-[#283D32] rounded-lg hover:border-[#C3A365]/50 transition-colors cursor-pointer"
                  >
                    <span className="text-white font-medium">£{amount}</span>
                  </div>
                ))}
              </div>
              <div className="p-4 bg-[#1E2F26] rounded-lg text-center">
                <p className="text-[#8BA89A] text-sm mb-1">{t('topup_custom')}</p>
                <p className="text-white">£10 — £500</p>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* CLOSED LOOP - Network */}
      <Section className="py-24 px-6 bg-[#18261E]">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-[#1E2F26] border border-[#283D32] px-4 py-2 rounded-full mb-8">
            <LockKey size={16} className="text-[#C3A365]" />
            <span className="text-sm text-[#8BA89A]">{t('section_network_badge')}</span>
          </div>

          <h2 className="text-3xl sm:text-4xl font-light tracking-tight mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {t('section_network_title')}
          </h2>

          <p className="text-[#8a9e91] max-w-2xl mx-auto mb-12 leading-relaxed">
            {t('section_network_desc')}
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
            {['🇫🇷 France', '🇬🇧 UK', '🇺🇸 USA', '🇯🇵 Japan', '🇩🇪 Germany', '🇮🇹 Italy', '🇪🇸 Spain', '🇨🇭 Switzerland', '🇦🇪 Dubai', '🇸🇬 Singapore', '🇭🇰 Hong Kong', '🇦🇺 Australia'].map((country, i) => (
              <div key={i} className="p-4 bg-[#12291c] border border-[#243d2f] text-center">
                <p className="text-white text-sm">{country}</p>
              </div>
            ))}
          </div>

          <button
            onClick={() => navigate('/reseau')}
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#C3A365] text-[#0F1A14] font-semibold"
          >
            <Storefront size={20} />
            {t('explore_network')}
          </button>
        </div>
      </Section>

      {/* POINTS & LEVELS */}
      <Section className="py-24 px-6 bg-[#0F1A14]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-[#C3A365] text-sm tracking-[0.2em] uppercase font-semibold mb-4">
              {t('section_loyalty')}
            </p>
            <h2 className="text-3xl sm:text-4xl font-light tracking-tight mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {t('section_loyalty_title')}
            </h2>
          </div>

          {/* Points explanation */}
          <div className="bg-[#18261E] border border-[#283D32] p-8 mb-8 text-center">
            <div className="flex items-center justify-center gap-8 mb-4">
              <div>
                <span className="text-5xl font-light text-white">2</span>
                <span className="text-[#8BA89A] ml-2">{t('points_per')}</span>
              </div>
              <span className="text-2xl text-[#8BA89A]">{t('points_by')}</span>
              <div>
                <span className="text-5xl font-light text-[#C3A365]">£1</span>
                <span className="text-[#8BA89A] ml-2">{t('points_spent')}</span>
              </div>
            </div>
            <p className="text-[#8BA89A]">100 {t('points')} = £1 {t('points_value')}</p>
          </div>

          {/* Tiers */}
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { name: 'Member', points: '0+', color: '#8BA89A', bonus: '2 pts/£1' },
              { name: 'Reserve', points: '1000+', color: '#C0C0C0', bonus: '3 pts/£1' },
              { name: 'Founding', points: '5000+', color: '#C3A365', bonus: '4 pts/£1 + VIP' }
            ].map((tier, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
                className={`p-6 text-center border ${
                  tier.name === 'Founding' 
                    ? 'bg-[#C3A365]/10 border-[#C3A365]/50' 
                    : 'bg-[#18261E] border-[#283D32]'
                }`}
              >
                <Crown size={32} style={{ color: tier.color }} weight="fill" className="mx-auto mb-3" />
                <h3 className="text-xl font-medium mb-1" style={{ color: tier.color }}>{tier.name}</h3>
                <p className="text-[#8BA89A] text-sm mb-2">{tier.points} pts</p>
                <p className="text-white font-medium">{tier.bonus}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* FOR MERCHANTS */}
      <Section className="py-24 px-6 bg-[#18261E]">
        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#1E2F26] border border-[#283D32] px-4 py-2 rounded-full mb-6">
                <Storefront size={16} className="text-[#C3A365]" />
                <span className="text-sm text-[#8BA89A]">{t('section_merchants')}</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-light tracking-tight mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                {t('section_merchants_title')}
              </h2>
              <p className="text-[#8BA89A] mb-8">
                {t('section_merchants_desc')}
              </p>

              <div className="space-y-3 mb-8">
                {[
                  t('merchants_qr'),
                  t('merchants_dashboard'),
                  t('merchants_commission'),
                  t('merchants_trial')
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <Check size={18} className="text-[#C3A365]" weight="bold" />
                    <span className="text-white">{item}</span>
                  </div>
                ))}
              </div>

              <button
                onClick={() => navigate('/tarifs-marchands')}
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#C3A365] text-[#0F1A14] font-semibold"
              >
                {t('become_partner')} <ArrowRight size={18} />
              </button>
            </div>

            <div className="bg-[#1E2F26] border border-[#283D32] p-8">
              <h3 className="text-xl text-white font-medium mb-6">{t('pricing')}</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center pb-4 border-b border-[#283D32]">
                  <span className="text-white">{t('trial')}</span>
                  <span className="text-[#C3A365] font-medium">{t('trial_free')}</span>
                </div>
                <div className="flex justify-between items-center pb-4 border-b border-[#283D32]">
                  <span className="text-white">Basic</span>
                  <span className="text-white font-medium">£29/mois</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-white">Advanced</span>
                  <span className="text-[#C3A365] font-medium">£79/mois</span>
                </div>
              </div>
              <p className="text-[#8BA89A] text-sm mt-6">{t('commission_note')}</p>
            </div>
          </div>
        </div>
      </Section>

      {/* FINAL CTA */}
      <Section className="py-32 px-6 bg-gradient-to-b from-[#0F1A14] to-[#18261E]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl sm:text-5xl font-light tracking-tight mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {t('final_title_1')}<br />
            <span className="text-[#C3A365]">{t('final_title_2')}</span>
          </h2>
          <p className="text-[#8BA89A] text-lg mb-10 max-w-xl mx-auto">
            {t('final_desc')}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <motion.button
              onClick={() => navigate('/offrir')}
              className="inline-flex items-center gap-3 bg-[#C3A365] text-[#0F1A14] px-10 py-5 text-lg font-semibold"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Gift size={22} />
              {t('final_cta')}
            </motion.button>
            <button
              onClick={() => navigate('/activer')}
              className="inline-flex items-center gap-2 px-8 py-4 border border-[#3E5248] text-white hover:border-[#C3A365]/50 transition-colors"
            >
              {t('have_card')}
            </button>
          </div>
        </div>
      </Section>

      {/* FOOTER */}
      <footer className="py-16 px-6 bg-[#0F1A14] border-t border-[#1E2F26]">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <span className="brand-logo text-2xl text-[#C3A365]">habitué</span>
              <p className="text-[#8BA89A] text-sm mt-4">
                {t('footer_tagline')}
              </p>
            </div>
            
            <div>
              <h4 className="text-white font-medium mb-4">{t('footer_members')}</h4>
              <ul className="space-y-2">
                <li><button onClick={() => navigate('/activer')} className="text-[#8BA89A] text-sm hover:text-[#C3A365]">{t('footer_activate')}</button></li>
                <li><button onClick={() => navigate('/offrir')} className="text-[#8BA89A] text-sm hover:text-[#C3A365]">{t('footer_gift')}</button></li>
                <li><button onClick={() => navigate('/reseau')} className="text-[#8BA89A] text-sm hover:text-[#C3A365]">{t('footer_shops')}</button></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-white font-medium mb-4">{t('footer_company')}</h4>
              <ul className="space-y-2">
                <li><button onClick={() => navigate('/a-propos')} className="text-[#8BA89A] text-sm hover:text-[#C3A365]">{t('nav_about')}</button></li>
                <li><button onClick={() => navigate('/comment-ca-marche')} className="text-[#8BA89A] text-sm hover:text-[#C3A365]">{t('hero_cta_secondary')}</button></li>
                <li><button onClick={() => navigate('/contact')} className="text-[#8BA89A] text-sm hover:text-[#C3A365]">{t('nav_contact')}</button></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-white font-medium mb-4">{t('footer_merchants')}</h4>
              <ul className="space-y-2">
                <li><button onClick={() => navigate('/tarifs-marchands')} className="text-[#8BA89A] text-sm hover:text-[#C3A365]">{t('footer_pricing')}</button></li>
                <li><button onClick={() => navigate('/register?role=merchant')} className="text-[#8BA89A] text-sm hover:text-[#C3A365]">{t('footer_partner')}</button></li>
              </ul>
            </div>
          </div>
          
          <div className="pt-8 border-t border-[#1E2F26] flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-[#8BA89A] text-sm">© 2025 habitué. {t('footer_rights')}</p>
            <p className="text-[#8BA89A] text-sm">{t('footer_love')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
